import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Video,
  Calendar,
  Clock,
  Users,
  Search,
  X,
  Disc,
  Mic,
  FileText,
  LogOut,
} from "react-feather";
import { getAttendeeMeetingsApi } from "../../../../api/api";
import "./MeetingPage.scss";

const STATUS_FILTERS = [
  { key: "all", label: "All" },
  { key: "active", label: "Live" },
  { key: "scheduled", label: "Upcoming" },
  { key: "ended", label: "Ended" },
  { key: "cancelled", label: "Cancelled" },
];

const MeetingList = () => {
  const navigate = useNavigate();
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });

  useEffect(() => {
    loadAttendeeMeetings();
  }, []);

  const loadAttendeeMeetings = async (page = 1) => {
    try {
      setLoading(true);
      setError(null);

      const response = await getAttendeeMeetingsApi({
        page,
        limit: 20,
      });

      if (response.data?.success) {
        setMeetings(response.data.data);
        setPagination(response.data.pagination);
      } else {
        setError("Failed to load meetings");
      }
    } catch (error) {
      console.error("Failed to load meetings:", error);
      setError(error.response?.data?.message || "Failed to load meetings");
    } finally {
      setLoading(false);
    }
  };

  // Clears any locally cached auth/session data and returns the user to
  // the login screen. Adjust the redirect path below if your login route
  // is different.
  const handleLogout = () => {
    localStorage.clear();
    navigate("/login");
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const date = new Date(dateStr);
    const today = new Date();

    if (date.toDateString() === today.toDateString()) {
      return "Today";
    }

    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    if (date.toDateString() === tomorrow.toDateString()) {
      return "Tomorrow";
    }

    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const getMeetingStatus = (meeting) => {
    if (meeting.meetingStatus === "CANCELLED") return "cancelled";
    if (meeting.isEnded || meeting.meetingStatus === "COMPLETED")
      return "ended";

    const now = new Date();
    const startTime = new Date(meeting.startTime);
    const endTime = new Date(meeting.endTime);

    if (now >= startTime && now <= endTime) return "active";
    if (now > endTime) return "ended";
    return "scheduled";
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case "active":
        return "mursal-meeting-list__card-badge--active";
      case "ended":
        return "mursal-meeting-list__card-badge--ended";
      case "cancelled":
        return "mursal-meeting-list__card-badge--cancelled";
      default:
        return "mursal-meeting-list__card-badge--scheduled";
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "active":
        return "Live";
      case "scheduled":
        return "Upcoming";
      case "ended":
        return "Ended";
      case "cancelled":
        return "Cancelled";
      default:
        return status;
    }
  };

  const getParticipantCount = (meeting) => meeting.participants?.length || 0;

  const initialOf = (participant) => {
    const source = participant.displayName || participant.email || "?";
    return source.trim()[0]?.toUpperCase() || "?";
  };

  // Compute each meeting's status once per fetch so search, the filter
  // pills, and the per-pill counts all read the same value instead of
  // recalculating getMeetingStatus on every render.
  const meetingsWithStatus = useMemo(
    () => meetings.map((m) => ({ ...m, _status: getMeetingStatus(m) })),
    [meetings],
  );

  const statusCounts = useMemo(() => {
    const counts = { all: meetingsWithStatus.length };
    for (const m of meetingsWithStatus) {
      counts[m._status] = (counts[m._status] || 0) + 1;
    }
    return counts;
  }, [meetingsWithStatus]);

  const filteredMeetings = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return meetingsWithStatus.filter((m) => {
      if (statusFilter !== "all" && m._status !== statusFilter) return false;
      if (!q) return true;

      const haystack = [
        m.title,
        m.description,
        m.organizerEmail,
        ...(m.participants || []).map((p) => p.email),
        ...(m.participants || []).map((p) => p.displayName),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(q);
    });
  }, [meetingsWithStatus, searchQuery, statusFilter]);

  const hasActiveFilters = searchQuery.trim() !== "" || statusFilter !== "all";

  const clearFilters = () => {
    setSearchQuery("");
    setStatusFilter("all");
  };

  if (loading) {
    return (
      <div className="mursal-loading">
        <div className="mursal-loading__spinner" />
        <p style={{ marginTop: 16, color: "#6b7280" }}>Loading meetings...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mursal-meeting-list">
        <div className="mursal-meeting-list__container">
          <div className="mursal-meeting-list__header">
            <div>
              <h1>Mursal UCC Meetings</h1>
              <p>Join meetings</p>
            </div>
            <div className="mursal-meeting-list__header-actions">
              <button
                className="mursal-meeting-list__logout-btn"
                onClick={handleLogout}
              >
                <LogOut size={16} />
                Logout
              </button>
            </div>
          </div>
          <div style={{ textAlign: "center", padding: "60px 20px" }}>
            <p style={{ color: "#ef4444", marginBottom: 16 }}>⚠️ {error}</p>
            <button
              className="mursal-meeting-list__create-btn"
              onClick={() => loadAttendeeMeetings()}
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mursal-meeting-list">
      <div className="mursal-meeting-list__container">
        <div className="mursal-meeting-list__header">
          <div>
            <h1>Mursal UCC Meetings</h1>
            <p>Join meetings</p>
          </div>
          <div className="mursal-meeting-list__header-actions">
            <button
              className="mursal-meeting-list__logout-btn"
              onClick={handleLogout}
            >
              <LogOut size={16} />
              Logout
            </button>
          </div>
        </div>

        {meetings.length === 0 ? (
          <div style={{ textAlign: "center", padding: "60px 20px" }}>
            <Video size={48} style={{ color: "#9ca3af", marginBottom: 16 }} />
            <h3 style={{ color: "#374151", marginBottom: 8 }}>
              No Meetings Found
            </h3>
            <p style={{ color: "#6b7280", marginBottom: 24 }}>
              You haven't been invited to any meetings yet.
            </p>
          </div>
        ) : (
          <>
            {/* Search + status filters */}
            <div className="mursal-meeting-list__toolbar">
              <div className="mursal-meeting-list__search">
                <Search size={16} />
                <input
                  type="text"
                  placeholder="Search by title, organizer, or participant..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                {searchQuery && (
                  <button
                    type="button"
                    className="mursal-meeting-list__search-clear"
                    onClick={() => setSearchQuery("")}
                    aria-label="Clear search"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>

              <div className="mursal-meeting-list__filters">
                {STATUS_FILTERS.map((f) => (
                  <button
                    key={f.key}
                    type="button"
                    className={`mursal-meeting-list__filter-pill ${
                      statusFilter === f.key
                        ? "mursal-meeting-list__filter-pill--active"
                        : ""
                    }`}
                    onClick={() => setStatusFilter(f.key)}
                  >
                    {f.label}
                    <span className="mursal-meeting-list__filter-count">
                      {statusCounts[f.key] || 0}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            <div className="mursal-meeting-list__results-meta">
              <span>
                Showing {filteredMeetings.length} of {meetings.length} meeting
                {meetings.length !== 1 ? "s" : ""}
              </span>
              {hasActiveFilters && (
                <button
                  type="button"
                  className="mursal-meeting-list__clear-filters"
                  onClick={clearFilters}
                >
                  Clear filters
                </button>
              )}
            </div>

            {filteredMeetings.length === 0 ? (
              <div style={{ textAlign: "center", padding: "60px 20px" }}>
                <Search
                  size={40}
                  style={{ color: "#9ca3af", marginBottom: 16 }}
                />
                <h3 style={{ color: "#374151", marginBottom: 8 }}>
                  No meetings match your filters
                </h3>
                <p style={{ color: "#6b7280", marginBottom: 24 }}>
                  Try a different search term or status.
                </p>
                <button
                  className="mursal-meeting-list__create-btn"
                  onClick={clearFilters}
                >
                  Clear filters
                </button>
              </div>
            ) : (
              <div className="mursal-meeting-list__grid">
                {filteredMeetings.map((meeting) => {
                  const status = meeting._status;
                  const isExchange = meeting.meetingSource === "EXCHANGE";
                  const participants = meeting.participants || [];
                  const visibleParticipants = participants.slice(0, 4);
                  const extraCount =
                    participants.length - visibleParticipants.length;

                  return (
                    <div
                      key={meeting.id}
                      className="mursal-meeting-list__card"
                      onClick={() => navigate(`/mursal-meetings/${meeting.id}`)}
                    >
                      <div className="mursal-meeting-list__card-top">
                        <div
                          className={`mursal-meeting-list__card-icon ${
                            isExchange
                              ? "mursal-meeting-list__card-icon--exchange"
                              : ""
                          }`}
                        >
                          {isExchange ? <Calendar /> : <Video />}
                        </div>
                        <div className="mursal-meeting-list__card-info">
                          <h5>{meeting.title}</h5>
                          <div className="mursal-meeting-list__card-meta">
                            <span
                              className={`mursal-meeting-list__card-badge ${getStatusBadgeClass(status)}`}
                            >
                              {getStatusLabel(status)}
                            </span>
                            {isExchange && (
                              <span className="mursal-meeting-list__card-source">
                                Exchange
                              </span>
                            )}
                            <span className="mursal-meeting-list__card-participants">
                              <Users size={14} />
                              {getParticipantCount(meeting)} participant
                              {getParticipantCount(meeting) !== 1 ? "s" : ""}
                            </span>
                          </div>
                        </div>
                      </div>

                      {meeting.description && (
                        <p className="mursal-meeting-list__card-description">
                          {meeting.description}
                        </p>
                      )}

                      {meeting.organizerEmail && (
                        <div className="mursal-meeting-list__card-organizer">
                          Organized by {meeting.organizerEmail}
                        </div>
                      )}

                      {participants.length > 0 && (
                        <div className="mursal-meeting-list__avatar-stack">
                          {visibleParticipants.map((p, i) => (
                            <span
                              key={p.id}
                              className="mursal-meeting-list__avatar"
                              style={{
                                background: `var(--mursal-avatar-${(i % 5) + 1})`,
                              }}
                              title={p.displayName || p.email}
                            >
                              {initialOf(p)}
                            </span>
                          ))}
                          {extraCount > 0 && (
                            <span className="mursal-meeting-list__avatar mursal-meeting-list__avatar--more">
                              +{extraCount}
                            </span>
                          )}
                        </div>
                      )}

                      {(meeting.recordingEnabled ||
                        meeting.transcriptEnabled ||
                        meeting.minutesEnabled) && (
                        <div className="mursal-meeting-list__feature-chips">
                          {meeting.recordingEnabled && (
                            <span className="mursal-meeting-list__feature-chip">
                              <Disc size={11} /> Recording
                            </span>
                          )}
                          {meeting.transcriptEnabled && (
                            <span className="mursal-meeting-list__feature-chip">
                              <Mic size={11} /> Transcript
                            </span>
                          )}
                          {meeting.minutesEnabled && (
                            <span className="mursal-meeting-list__feature-chip">
                              <FileText size={11} /> Minutes
                            </span>
                          )}
                        </div>
                      )}

                      <div className="mursal-meeting-list__card-bottom">
                        <div className="mursal-meeting-list__card-datetime">
                          <span>
                            <Calendar size={14} />
                            {formatDate(meeting.startTime)}
                          </span>
                          <span>
                            <Clock size={14} />
                            {formatTime(meeting.startTime)}
                          </span>
                        </div>
                        {status === "active" && (
                          <button
                            className="mursal-meeting-list__card-join-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/mursal-meetings/${meeting.id}`);
                            }}
                          >
                            Join Now
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  gap: "8px",
                  marginTop: "24px",
                  padding: "16px 0",
                }}
              >
                <button
                  className="mursal-meeting-list__create-btn"
                  style={{ padding: "8px 16px", fontSize: "13px" }}
                  disabled={pagination.page <= 1}
                  onClick={() => loadAttendeeMeetings(pagination.page - 1)}
                >
                  Previous
                </button>
                <span
                  style={{
                    display: "flex",
                    alignItems: "center",
                    padding: "0 16px",
                    color: "#6b7280",
                    fontSize: "13px",
                  }}
                >
                  Page {pagination.page} of {pagination.totalPages}
                </span>
                <button
                  className="mursal-meeting-list__create-btn"
                  style={{ padding: "8px 16px", fontSize: "13px" }}
                  disabled={pagination.page >= pagination.totalPages}
                  onClick={() => loadAttendeeMeetings(pagination.page + 1)}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default MeetingList;
